import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Select, Table, Tag, Typography, Space, message, Input } from 'antd';
import { TableOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;

interface Part {
  job_num: string;
  part_name: string;
  part_number: string;
  model: string;
}

const MatrixMappingPage: React.FC = () => {
  const navigate = useNavigate();
  const [systems, setSystems] = useState<string[]>([]);
  const [selectedSystem, setSelectedSystem] = useState<string | null>(null);
  const [parts, setParts] = useState<Part[]>([]);
  const [loadingSystems, setLoadingSystems] = useState(false);
  const [loadingParts, setLoadingParts] = useState(false);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    fetchSystems();
  }, []);

  const fetchSystems = async () => {
    setLoadingSystems(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/system_names`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch systems');
      const data = await res.json();
      setSystems(data);
    } catch {
      message.error('Failed to load systems');
    } finally {
      setLoadingSystems(false);
    }
  };

  const fetchParts = async (systemName: string) => {
    setLoadingParts(true);
    setParts([]);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/matrix-mapping/parts?system_name=${encodeURIComponent(systemName)}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Failed to fetch parts');
      const data = await res.json();
      setParts(data.parts);
    } catch {
      message.error('Failed to load parts');
    } finally {
      setLoadingParts(false);
    }
  };

  const handleSystemChange = (value: string) => {
    setSelectedSystem(value);
    setSearchText('');
    fetchParts(value);
  };

  const filteredParts = useMemo(() => {
    if (!searchText) return parts;
    const lower = searchText.toLowerCase();
    return parts.filter(
      (p) =>
        String(p.job_num ?? '').toLowerCase().includes(lower) ||
        String(p.part_name ?? '').toLowerCase().includes(lower) ||
        String(p.part_number ?? '').toLowerCase().includes(lower) ||
        String(p.model ?? '').toLowerCase().includes(lower)
    );
  }, [parts, searchText]);

  const columns: ColumnsType<Part> = [
    {
      title: 'Job #',
      dataIndex: 'job_num',
      key: 'job_num',
      sorter: (a, b) => (a.job_num || '').localeCompare(b.job_num || ''),
      width: 120,
    },
    {
      title: 'Part Name',
      dataIndex: 'part_name',
      key: 'part_name',
      sorter: (a, b) => (a.part_name || '').localeCompare(b.part_name || ''),
    },
    {
      title: 'Part Number',
      dataIndex: 'part_number',
      key: 'part_number',
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      render: (val: string) => val ? <Tag color='blue'>{val}</Tag> : '—',
    },
    {
      title: '',
      key: 'action',
      width: 40,
      render: () => <RightOutlined style={{ color: '#bfbfbf' }} />,
    },
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', padding: 24 }}>

      {/* Header */}
      <Card style={{ marginBottom: 24, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 12 }}>
        <Space align='center'>
          <TableOutlined style={{ fontSize: 28, color: '#1890ff' }} />
          <div>
            <Title level={3} style={{ margin: 0, color: '#1890ff' }}>Matrix Mapping</Title>
            <Text type='secondary'>Select a system and part to configure camera view labels</Text>
          </div>
        </Space>
      </Card>

      {/* System Selector */}
      <Card style={{ marginBottom: 24, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 12 }}>
        <Text strong style={{ display: 'block', marginBottom: 10 }}>System</Text>
        <Select
          style={{ width: 360 }}
          placeholder='Choose a system to load parts'
          loading={loadingSystems}
          options={systems.map((s) => ({ label: s, value: s }))}
          onChange={handleSystemChange}
          value={selectedSystem}
          size='large'
        />
      </Card>

      {/* Parts Table */}
      {selectedSystem && (
        <Card style={{ border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 12 }}>
          <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
            <Text strong style={{ fontSize: 15 }}>
              Parts — <Text type='secondary' style={{ fontWeight: 400 }}>{selectedSystem}</Text>
              {searchText && (
                <Text type='secondary' style={{ fontWeight: 400, marginLeft: 8 }}>
                  ({filteredParts.length} of {parts.length})
                </Text>
              )}
            </Text>
            <Input
              placeholder='Search job #, part name, number, model…'
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{ width: 320 }}
            />
          </Space>
          <Table<Part>
            rowKey='job_num'
            columns={columns}
            dataSource={filteredParts}
            loading={loadingParts}
            pagination={false}
            scroll={{ y: 520 }}
            onRow={(record) => ({
              onClick: () =>
                navigate(`/matrix-mapping/views/${encodeURIComponent(selectedSystem)}/${encodeURIComponent(record.job_num)}`),
              style: { cursor: 'pointer' },
            })}
            locale={{ emptyText: 'No parts found for this system' }}
            rowClassName={() => 'matrix-mapping-row'}
          />
        </Card>
      )}
    </div>
  );
};

export default MatrixMappingPage;
